/**
 * @fileoverview Role Manager component for assigning and managing employee roles.
 * Provides UI for administrators to assign roles to employees.
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserShield, faInfo, faCheck, faBuilding, faTimes, faSearch } from '@fortawesome/free-solid-svg-icons';
import { ROLES, getRoleDisplayName, getRoleDescription, getRoleColor } from '../../utils/permissions';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import './RoleManager.css';

/**
 * RoleManager component for managing employee roles
 * @param {Object} props - Component props
 * @param {Object} props.employee - Employee object
 * @param {Function} props.onRoleUpdate - Callback when role is updated
 * @returns {React.ReactElement} The rendered component
 */
const RoleManager = ({ employee, onRoleUpdate }) => {
  const [selectedRole, setSelectedRole] = useState(employee.role || 'csm');
  const [saving, setSaving] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState(employee.assigned_organizations || []);
  const [orgSearch, setOrgSearch] = useState('');
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);

  const roles = Object.values(ROLES);

  // Sync with employee prop when it changes (e.g., after save refreshes parent)
  useEffect(() => {
    setSelectedOrganizations(employee.assigned_organizations || []);
    setSelectedRole(employee.role || 'csm');
  }, [employee]);

  // Fetch organizations when component mounts
  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOrgDropdown && !event.target.closest('.org-search-container')) {
        setShowOrgDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOrgDropdown]);

  const fetchOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      const response = await axios.get(
        'https://track.snapped.cc/api/partners',
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data) {
        setOrganizations(response.data);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleRoleChange = async (newRole) => {
    if (newRole === employee.role) return;
    setSelectedRole(newRole);
    
    // If switching to contractor role, don't save immediately - wait for org selection
    if (newRole === ROLES.CONTRACTOR) {
      return;
    }
    
    // For non-contractor roles, save immediately
    await saveRole(newRole, []);
  };

  const saveRole = async (role, assignedOrgs) => {
    setSaving(true);
    try {
      const { tokens } = await fetchAuthSession();
      const token = tokens.idToken.toString();

      // Update employee role in the database
      const updateData = { 
        role: role,
        assigned_organizations: assignedOrgs
      };

      const response = await axios.put(
        `https://track.snapped.cc/api/employees/${employee.user_id}`,
        updateData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.success !== false) {
        toast.success(`Role updated to ${getRoleDisplayName(role)}`);
        
        // Update local state with the saved organizations from response
        if (response.data.assigned_organizations !== undefined) {
          setSelectedOrganizations(response.data.assigned_organizations);
        }
        
        // Call the callback to refresh the employee data in parent
        if (onRoleUpdate) {
          onRoleUpdate(employee.user_id, role, assignedOrgs);
        }
      } else {
        throw new Error('Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update role. Please try again.');
      // Revert role selection on error
      setSelectedRole(employee.role || 'csm');
    } finally {
      setSaving(false);
    }
  };

  const handleOrganizationToggle = (orgId) => {
    const isRemoving = selectedOrganizations.includes(orgId);
    const newOrgs = isRemoving
      ? selectedOrganizations.filter(id => id !== orgId)
      : [...selectedOrganizations, orgId];
    
    setSelectedOrganizations(newOrgs);
  };

  const handleSaveContractorRole = async () => {
    if (selectedOrganizations.length === 0) {
      toast.error('Please select at least one organization for the contractor');
      return;
    }
    await saveRole(ROLES.CONTRACTOR, selectedOrganizations);
    // State is already correct, no need to do anything else
  };

  const getOrganizationName = (orgId) => {
    const org = organizations.find(o => o.id === orgId || o._id === orgId);
    return org ? org.name : orgId;
  };

  const filteredOrganizations = organizations.filter(org =>
    org.name?.toLowerCase().includes(orgSearch.toLowerCase())
  );

  return (
    <div className="role-manager">
      <div className="role-manager-header">
        <FontAwesomeIcon icon={faUserShield} />
        <h3>Role Assignment</h3>
      </div>

      <div className="role-options">
        {roles.map(role => (
          <div
            key={role}
            className={`role-option ${selectedRole === role ? 'selected' : ''} ${saving ? 'disabled' : ''}`}
            onClick={() => !saving && handleRoleChange(role)}
            style={{
              borderColor: selectedRole === role ? getRoleColor(role) : '#e5e7eb'
            }}
          >
            <div className="role-option-header">
              <div
                className="role-badge"
                style={{ backgroundColor: getRoleColor(role) }}
              >
                {getRoleDisplayName(role)}
              </div>
              {selectedRole === role && (
                <FontAwesomeIcon icon={faCheck} className="role-check" />
              )}
            </div>
            <div className="role-description">
              <FontAwesomeIcon icon={faInfo} className="role-info-icon" />
              {getRoleDescription(role)}
            </div>
          </div>
        ))}
      </div>

      {/* Organization Selector for Contractor Role */}
      {selectedRole === ROLES.CONTRACTOR && (
        <div className="contractor-organizations">
          <div className="contractor-org-header">
            <FontAwesomeIcon icon={faBuilding} />
            <h4>Assigned Organizations</h4>
            <span className="org-count">({selectedOrganizations.length} selected)</span>
          </div>

          {/* Selected Organizations Display */}
          {selectedOrganizations.length > 0 && (
            <div className="selected-organizations">
              {loadingOrgs ? (
                <div className="org-loading-placeholder">Loading organizations...</div>
              ) : (
                selectedOrganizations.map(orgId => (
                  <div key={orgId} className="org-badge">
                    <span>{getOrganizationName(orgId)}</span>
                    <button
                      onClick={() => handleOrganizationToggle(orgId)}
                      className="org-badge-remove"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Organization Search and Selector */}
          <div className="org-search-container">
            <div className="org-search-input-wrapper">
              <FontAwesomeIcon icon={faSearch} className="org-search-icon" />
              <input
                type="text"
                placeholder="Search organizations..."
                value={orgSearch}
                onChange={(e) => setOrgSearch(e.target.value)}
                onFocus={() => setShowOrgDropdown(true)}
                className="org-search-input"
              />
            </div>

            {showOrgDropdown && (
              <div className="org-dropdown">
                {loadingOrgs ? (
                  <div className="org-dropdown-loading">Loading organizations...</div>
                ) : filteredOrganizations.length > 0 ? (
                  filteredOrganizations.map(org => (
                    <div
                      key={org.id}
                      className={`org-dropdown-item ${selectedOrganizations.includes(org.id) ? 'selected' : ''}`}
                      onClick={() => handleOrganizationToggle(org.id)}
                    >
                      <div className="org-dropdown-item-content">
                        <span>{org.name}</span>
                        {selectedOrganizations.includes(org.id) && (
                          <FontAwesomeIcon icon={faCheck} className="org-check" />
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="org-dropdown-empty">No organizations found</div>
                )}
              </div>
            )}
          </div>

          {/* Save Button for Contractor Role */}
          <button
            className="contractor-save-btn"
            onClick={handleSaveContractorRole}
            disabled={saving || selectedOrganizations.length === 0}
          >
            {saving ? 'Saving...' : `Save Contractor Role with ${selectedOrganizations.length} Organization(s)`}
          </button>
        </div>
      )}

      {saving && selectedRole !== ROLES.CONTRACTOR && (
        <div className="role-saving-indicator">
          Updating role...
        </div>
      )}
    </div>
  );
};

export default RoleManager;

